import { useEffect, useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { Schema } from "effect";
import { getFormErrorMessage } from "../../../../../lib/forms/form-errors";
import { useTranslation } from "react-i18next";
import { Building2, Check, User } from "lucide-react";
import {
  useCheckInSend,
  useCheckInSelector,
} from "../context";
import { checkInSelectors } from "../selectors";
import type { MatchedPatientInfo } from "../machine";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { useAudio } from "../../../../../lib/use-audio";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { KioskStep } from "../../../../../components/kiosk-step";
import { KioskTextField } from "../../../../../components/kiosk-field";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import { useCardAction } from "../../use-card-action";

const CHECKIN_PATIENT_SELECTION_FIRSTNAME_ID =
  "frontdesk-patient-selection-firstname-input";

const PatientSelectionSchema = Schema.Struct({
  firstname: Schema.String.pipe(
    Schema.check(Schema.isMinLength(1, { message: "validation.required" })),
  ),
});

/**
 * Normalize a string for fuzzy comparison: lowercase, trim, collapse whitespace.
 */
function normalize(s: string): string {
  return s.toLowerCase().trim().replace(/\s+/g, " ");
}

/**
 * Fuzzy-match a user-entered firstname against the matched patients' given names.
 * Returns all patients whose givenName starts with or contains the input.
 * Prefers startsWith matches over contains matches.
 */
function fuzzyMatchPatients(
  input: string,
  patients: MatchedPatientInfo[],
): MatchedPatientInfo[] {
  const needle = normalize(input);
  if (!needle) return [];

  const startsWithMatches = patients.filter((p) =>
    normalize(p.givenName).startsWith(needle),
  );
  if (startsWithMatches.length > 0) return startsWithMatches;

  return patients.filter((p) => normalize(p.givenName).includes(needle));
}

/**
 * Patient selection step: Firstname disambiguation when multiple patients match.
 *
 * When the eGK card read returns MATCHING with multiple patients, this step
 * asks the user to enter their first name. The firstname is fuzzy-matched
 * against the matched patients locally (no patient data displayed on screen).
 *
 * - Unique match: calls selectPatient RPC, dispatches PATIENT_SELECTED
 * - No match or still ambiguous: shows an inline error to retry or go back
 *
 * @since 0.1.0
 * @example
 * ```tsx
 * <PatientSelectionStep />
 * ```
 */
export function PatientSelectionStep() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const matchedPatients = useCheckInSelector(checkInSelectors.matchedPatients);
  const crlogId = useCheckInSelector(checkInSelectors.crlogId);
  const egkAttestation = useCheckInSelector(checkInSelectors.egkAttestation);

  const { run, isPending: isSelecting } = useCardAction();
  const [error, setError] = useState<string>();

  useAudio("checkIn-patientSelection");

  useEffect(function runFocusEffect() {
    document.getElementById(CHECKIN_PATIENT_SELECTION_FIRSTNAME_ID)?.focus();
  }, []);

  const form = useForm({
    defaultValues: {
      firstname: "",
    },
    validators: {
      onChange: Schema.toStandardSchemaV1(PatientSelectionSchema),
    },
    onSubmit: async ({ value }) => {
      if (!value.firstname.trim() || !matchedPatients || !crlogId) return;

      const matches = fuzzyMatchPatients(value.firstname, matchedPatients);

      if (matches.length === 0) {
        setError(t("self-service.check-in.patient-selection.no-match-v2"));
        return;
      }

      if (matches.length > 1) {
        setError(t("self-service.check-in.patient-selection.still-ambiguous-v2"));
        return;
      }

      const [selectedPatient] = matches;
      if (!selectedPatient) return;
      setError(undefined);

      try {
        const result = await run(() => docCirrusRpcClient.frontdesk.selectPatient({
          crlogId,
          patientId: selectedPatient.id,
        }));
        if (!result) return;
        if (result.crlogId !== crlogId || result.validationStatus !== "OK" ||
            (result.status !== "APPLIED" && result.status !== "MERGED") ||
            (result.patientId && result.patientId !== selectedPatient.id) ||
            (result.status === "MERGED" && (!result.possibleActions.includes("apply") || !result.diff))) {
          throw new Error("Patient selection did not reach a confirmed workflow state");
        }

        send({
          type: "PATIENT_SELECTED",
          patientId: result.patientId ?? selectedPatient.id,
          crlogId: result.crlogId,
          hasDiff: result.status === "MERGED",
          diffData: result.diff ? [...result.diff] : undefined,
          egkAttestation: result.egkAttestation ?? egkAttestation,
        });
      } catch (err) {
        frontdeskDiagnosticLogger.error("Failed to select patient", {
          error: err,
          hasSelectedPatientId: Boolean(selectedPatient.id),
          hasCrlogId: Boolean(crlogId),
        });
        setError(t("self-service.check-in.patient-selection.select-error-v2"));
      }
    },
  });

  const firstname = useStore(form.store, (state) => state.values.firstname);
  const canSubmit = useStore(form.store, (state) => state.canSubmit);

  return (
    <KioskStep
      width="narrow"
      title={t("self-service.check-in.patient-selection.title-v2")}
      lead={t("self-service.check-in.patient-selection.subtitle-v2")}
      primaryAction={{
        label: t("common.confirm"),
        icon: Check,
        onClick: () => void form.handleSubmit(),
        disabled: !canSubmit || firstname.trim().length === 0,
        busy: isSelecting,
        testId: "frontdesk-patient-selection-submit-btn",
      }}
      secondaryAction={
        error === undefined
          ? undefined
          : {
              label: t("self-service.help-reception"),
              icon: Building2,
              onClick: () => send({ type: "RECEPTION_HELP" }),
              testId: "frontdesk-patient-selection-reception-btn",
            }
      }
    >
      <form
        data-testid="frontdesk-patient-selection-form"
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.Field name="firstname">
          {(field) => (
            <KioskTextField
              id={CHECKIN_PATIENT_SELECTION_FIRSTNAME_ID}
              testId="frontdesk-patient-selection-firstname-input"
              label={t("self-service.check-in.patient-selection.firstname-label")}
              icon={User}
              required
              autoComplete="given-name"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(value) => {
                field.handleChange(value);
                setError(undefined);
              }}
              disabled={isSelecting}
              error={
                field.state.meta.isBlurred && field.state.meta.errors.length > 0
                  ? t(getFormErrorMessage(field.state.meta.errors[0]))
                  : undefined
              }
            />
          )}
        </form.Field>

        {error ? (
          <KioskStatePanel
            tone="warning"
            title={error}
            testId="frontdesk-patient-selection-error"
          />
        ) : null}
      </form>
    </KioskStep>
  );
}
